/** Talking Heads Studio — fit modes, template helpers, avatar box sizing. */

export const CONTENT_FIT_MODES = [
  { id: 'cover', label: 'Cover', desc: 'Fill content box — crops edges if needed' },
  { id: 'contain', label: 'Contain', desc: 'Fit inside box — letterbox if needed' },
  { id: 'fill', label: 'Stretch', desc: 'Stretch to exact box (legacy)' },
];

export const CLIP_FIT_MODES = [
  { id: 'contain', label: 'Contain', desc: 'Clip fits inside box — default for external clips' },
  { id: 'cover', label: 'Cover', desc: 'Clip fills box — may crop' },
  { id: 'fill', label: 'Stretch', desc: 'Stretch clip to exact box' },
];

const LOCAL_KEY = (user) =>
  `talking_heads_templates_${user?.email || user?._id || user?.id || 'guest'}`;

export function loadLocalTemplates(user) {
  try {
    const raw = localStorage.getItem(LOCAL_KEY(user));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveLocalTemplates(user, templates) {
  try {
    localStorage.setItem(LOCAL_KEY(user), JSON.stringify(templates));
  } catch {
    /* ignore quota errors */
  }
}

export function defaultClipLayer(canvasW, canvasH) {
  const w = Math.round(canvasW * 0.72);
  const h = Math.round(canvasH * 0.72);
  return {
    x: Math.round((canvasW - w) / 2),
    y: Math.round((canvasH - h) / 2),
    width: w,
    height: h,
  };
}

/** Build a storable layout_config snapshot from studio state. */
export function buildLayoutConfigSnapshot({
  selectedLayout,
  canvasW,
  canvasH,
  avatarLayer,
  contentLayer,
  clipLayer,
  avatarBorder,
  contentBorder,
  clipBorder,
  resolvedBackground,
  contentFit,
  clipFit,
  outputPreset,
}) {
  const buildLayer = (layer, border) => {
    if (!layer) return null;
    return {
      x: Math.round(layer.x),
      y: Math.round(layer.y),
      width: Math.round(layer.width),
      height: Math.round(layer.height),
      border: {
        width: Math.max(0, Math.round(border?.width ?? 0)),
        color: /^#[0-9a-fA-F]{6}$/.test(border?.color ?? '') ? border.color : '#ffffff',
        radius: Math.max(0, Math.round(border?.radius ?? 0)),
        style: border?.style ?? 'solid',
      },
    };
  };

  if (!selectedLayout) return null;

  return {
    layout_id: selectedLayout.id,
    output_preset: outputPreset,
    canvas: { width: canvasW, height: canvasH },
    avatar_layer: buildLayer(avatarLayer, avatarBorder),
    content_layer: buildLayer(contentLayer, contentBorder),
    clip_layer: buildLayer(clipLayer, clipBorder),
    background_layer: selectedLayout.background,
    background: resolvedBackground,
    content_fit: contentFit || 'cover',
    clip_fit: clipFit || 'contain',
  };
}

/** Apply a saved layout_config back into studio state setters. */
export function applyLayoutConfigSnapshot(config, {
  layouts,
  setOutputPreset,
  applyLayout,
  setAvatarLayer,
  setContentLayer,
  setClipLayer,
  setAvatarBorder,
  setContentBorder,
  setClipBorder,
  setBackground,
  setCustomBgColor,
  setContentFit,
  setClipFit,
}) {
  if (!config) return false;

  if (config.output_preset) {
    setOutputPreset(config.output_preset);
  }

  const layout = layouts.find((l) => l.id === config.layout_id) || layouts[0];
  if (layout) applyLayout(layout);

  if (config.avatar_layer) setAvatarLayer({ ...config.avatar_layer });
  if (config.content_layer) setContentLayer({ ...config.content_layer });
  if (config.clip_layer) {
    setClipLayer({ ...config.clip_layer });
  } else if (config.canvas) {
    setClipLayer(defaultClipLayer(config.canvas.width, config.canvas.height));
  }

  const avBorder = config.avatar_layer?.border;
  if (avBorder) {
    setAvatarBorder({
      width: avBorder.width ?? 0,
      color: avBorder.color ?? '#7c6af7',
      radius: avBorder.radius ?? 0,
      style: avBorder.style ?? 'solid',
    });
  }

  const ctBorder = config.content_layer?.border;
  if (ctBorder) {
    setContentBorder({
      width: ctBorder.width ?? 0,
      color: ctBorder.color ?? '#22c55e',
      radius: ctBorder.radius ?? 0,
      style: ctBorder.style ?? 'solid',
    });
  }

  const clBorder = config.clip_layer?.border;
  if (clBorder) {
    setClipBorder({
      width: clBorder.width ?? 0,
      color: clBorder.color ?? '#60a5fa',
      radius: clBorder.radius ?? 0,
      style: clBorder.style ?? 'solid',
    });
  }

  if (config.content_fit) setContentFit(config.content_fit);
  if (config.clip_fit) setClipFit(config.clip_fit);

  const bg = config.background || 'blurred';
  if (bg === 'blurred') {
    setBackground('blurred');
  } else if (typeof bg === 'string' && bg.startsWith('image:')) {
    setBackground('image');
  } else if (typeof bg === 'string' && bg.startsWith('video:')) {
    setBackground('video');
  } else if (typeof bg === 'string' && bg.startsWith('#')) {
    setBackground('custom');
    setCustomBgColor(bg);
  }

  return true;
}

/** Size avatar box to video aspect ratio without stretching. */
export function fitAvatarBoxToVideo(videoDims, canvasW, canvasH, currentLayer) {
  if (!videoDims?.w || !videoDims?.h || !currentLayer) return currentLayer;

  const ratio = videoDims.w / videoDims.h;
  const maxW = Math.round(canvasW * 0.46);
  const maxH = Math.round(canvasH * 0.90);

  let w = currentLayer.width;
  let h = Math.round(w / ratio);

  if (h > maxH) {
    h = maxH;
    w = Math.round(h * ratio);
  }
  if (w > maxW) {
    w = maxW;
    h = Math.round(w / ratio);
  }

  const x = Math.max(0, Math.min(currentLayer.x, canvasW - w));
  const y = Math.max(0, Math.min(currentLayer.y, canvasH - h));

  return { ...currentLayer, x, y, width: w, height: h };
}
